import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageMetadata from '../utils/usePageMetadata';
import './FeaturePage.css';

const AnalyticsFeatures = () => {
  const { token } = useAuth();

  usePageMetadata({
    title: 'Analytics Dashboard | Unified GA4, AdSense & Search Console Analytics',
    description:
      'Unify Google Analytics 4, AdSense, and Google Search Console data in one powerful dashboard. Track performance, revenue, and search metrics with real-time insights.',
    canonicalUrl: 'https://theadshop.co/analytics-features',
    meta: [
      { property: 'og:title', content: 'Analytics Dashboard | Unified GA4, AdSense & Search Console Analytics' },
      {
        property: 'og:description',
        content:
          'Bring together GA4, AdSense, and Search Console metrics in one dashboard with automated insights and advanced filtering.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/analytics-features' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:title', content: 'Analytics Dashboard | Unified GA4, AdSense & Search Console Analytics' },
      {
        name: 'twitter:description',
        content:
          'Unify your analytics data and get actionable insights from GA4, AdSense, and Search Console in one place.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Analytics Dashboard Features | The Ad Shop',
        url: 'https://theadshop.co/analytics-features',
        description:
          'Learn about The Ad Shop Analytics Dashboard features including unified GA4, AdSense, and Search Console data, real-time metrics, interactive visualizations, and automated insights.',
        isPartOf: {
          '@type': 'WebSite',
          name: 'The Ad Shop',
          url: 'https://theadshop.co/',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://theadshop.co/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Analytics Dashboard',
              item: 'https://theadshop.co/analytics-features',
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'The Ad Shop Analytics Dashboard',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0.00',
          priceCurrency: 'USD',
        },
        featureList: [
          'Unified Google Analytics 4, AdSense, and Search Console data',
          'Real-time traffic metrics and revenue tracking',
          'Interactive charts and trend visualizations',
          'Advanced filtering by device, geography, and traffic source',
          'Automated insights and recommendations',
          'Search performance data integration',
        ],
        description:
          'Unified analytics dashboard that brings together Google Analytics 4, AdSense, and Google Search Console data with real-time insights and automated recommendations.',
      },
    ],
  });

  return (
    <div className="feature-page">
      <div className="feature-container">
        <div className="feature-header">
          <div className="feature-icon-large">📊</div>
          <h1>Analytics Dashboard</h1>
          <p className="feature-subtitle">
            Unify Google Analytics 4, AdSense, and Google Search Console data in one powerful view. 
            Track performance, revenue, and search metrics with real-time insights.
          </p>
        </div>

        <div className="feature-content">
          <section className="feature-section">
            <h2>Unified Data Sources</h2>
            <p>
              Stop switching between multiple tools. The Analytics Dashboard brings together your most important 
              data sources in one unified interface:
            </p>
            <ul className="feature-list">
              <li><strong>Google Analytics 4:</strong> Complete traffic and engagement metrics including users, sessions, pageviews, and engagement rates</li>
              <li><strong>Google AdSense:</strong> Revenue tracking, RPM analysis, and earnings breakdowns by page and traffic source</li>
              <li><strong>Google Search Console:</strong> Search performance data, click-through rates, impressions, and average position</li>
            </ul>
            <p>
              All your data syncs automatically through secure OAuth connections, so you always have the latest 
              information at your fingertips.
            </p>
          </section>

          <section className="feature-section">
            <h2>Real-Time Metrics & Insights</h2>
            <p>
              Get up-to-date metrics and trends with automatic data synchronization. Track everything that matters:
            </p>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">👥</div>
                <h3>Users & Sessions</h3>
                <p>Track new and returning users with detailed session analytics and comparison to previous periods</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">👁️</div>
                <h3>Pageviews</h3>
                <p>Monitor page performance and user engagement metrics across your entire site</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">❤️</div>
                <h3>Engagement Rate</h3>
                <p>Measure how users interact with your content with engagement rate and average engagement time</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">💰</div>
                <h3>Revenue & RPM</h3>
                <p>Track AdSense earnings, revenue per user, and RPM metrics to understand monetization performance</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🔍</div>
                <h3>Search Performance</h3>
                <p>Monitor search impressions, clicks, CTR, and average position from Google Search Console</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📈</div>
                <h3>Trend Analysis</h3>
                <p>Compare current performance to previous periods with percentage change indicators</p>
              </div>
            </div>
          </section>

          <section className="feature-section">
            <h2>Interactive Visualizations</h2>
            <p>
              Beautiful charts and visualizations make complex data easy to understand at a glance:
            </p>
            <ul className="feature-list">
              <li><strong>Traffic Trend Charts:</strong> Interactive line charts showing pageviews, users, and sessions over time with toggleable metrics</li>
              <li><strong>Top Pages Table:</strong> Ranked list of your best-performing pages with engagement metrics and color-coded badges</li>
              <li><strong>Device Breakdown:</strong> Understand desktop, tablet, and mobile traffic distribution</li>
              <li><strong>Geographic Data:</strong> See where your traffic comes from with top geography breakdowns</li>
              <li><strong>Traffic Source Analysis:</strong> Identify which sources drive the most traffic and revenue</li>
            </ul>
          </section>

          <section className="feature-section">
            <h2>Advanced Filtering & Segmentation</h2>
            <p>
              Slice your data across multiple dimensions effortlessly. Filter by:
            </p>
            <ul className="feature-list">
              <li><strong>Device Type:</strong> Desktop, mobile, tablet</li>
              <li><strong>Traffic Source:</strong> Organic, direct, referral, social, paid</li>
              <li><strong>Geography:</strong> Country, region, city-level analysis</li>
              <li><strong>Time Range:</strong> Custom date ranges, 7-day, 14-day, 30-day views</li>
              <li><strong>Social Media Platforms:</strong> Filter by specific platforms like Facebook, Twitter, Instagram</li>
            </ul>
            <p>
              Combine multiple filters to drill down into exactly the data you need to make informed decisions.
            </p>
          </section>

          <section className="feature-section">
            <h2>Automated Insights & Recommendations</h2>
            <p>
              Our insights engine automatically analyzes your data and surfaces actionable recommendations:
            </p>
            <ul className="feature-list">
              <li><strong>High Engagement, Low Traffic:</strong> Discover pages with above-average engagement that need promotion</li>
              <li><strong>Declining Traffic:</strong> Get alerts when pages experience significant traffic drops</li>
              <li><strong>Engagement Mismatch:</strong> Identify pages that need CTAs or layout improvements</li>
              <li><strong>Trending Content:</strong> Spot pages with rapid growth to double down on similar content</li>
              <li><strong>Revenue Opportunities:</strong> Find pages with high traffic but low revenue potential</li>
            </ul>
            <p>
              Each insight includes an impact score (0-100) to help you prioritize which actions will have the biggest impact.
            </p>
          </section>

          <section className="feature-section">
            <h2>Easy Setup & Integration</h2>
            <p>
              Connect your Google accounts in minutes through our secure OAuth workflow:
            </p>
            <ul className="feature-list">
              <li>One-click connection to Google Analytics 4</li>
              <li>Secure AdSense account linking</li>
              <li>Google Search Console property integration</li>
              <li>Automatic data synchronization</li>
              <li>No coding or technical expertise required</li>
            </ul>
            <p>
              Once connected, your data syncs automatically based on your plan's refresh interval. Free plans refresh 
              every 6 hours, while paid plans can refresh as frequently as every hour or continuously.
            </p>
          </section>

          <section className="feature-cta">
            <h2>Ready to Unify Your Analytics?</h2>
            <p>
              Start tracking your GA4, AdSense, and Search Console data in one powerful dashboard today.
            </p>
            {token ? (
              <Link to="/dashboard" className="btn-primary">
                Go to Analytics Dashboard
              </Link>
            ) : (
              <Link to="/signup" className="btn-primary">
                Sign Up Free
              </Link>
            )}
            <p className="cta-note">Free to get started • No credit card required • Setup in minutes</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFeatures;

